import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Electrician = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my electrical business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps electricians capture 95% of emergency calls 24/7, respond to enquiries in under 3 minutes, and convert 75% of quotes to bookings. Features include emergency call capture, instant quote calculator, and automated maintenance reminders."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for electricians?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most electricians recoup the cost within the first week through reduced missed calls and faster quote responses."
        }
      },
      {
        "@type": "Question",
        "name": "Will it capture emergency calls after hours?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The 24/7 emergency call capture system ensures you never miss power outage or electrical emergency calls, even at 3am. Calls are logged instantly with SMS confirmation to customers."
        }
      },
      {
        "@type": "Question",
        "name": "Can it help with commercial clients?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. The commercial client portal lets property managers submit maintenance requests 24/7 and view job history. Electricians report signing 8+ commercial contracts using this professional system."
        }
      },
      {
        "@type": "Question",
        "name": "Does it work for annual inspection reminders?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Automated annual electrical inspection reminders help build recurring revenue. Electricians report 127+ customers on automated annual inspection schedules, with 40% adding extra work during visits."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Electricians - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Electrician CRM", url: "/industries/electrician" }
  ];

  const industryData = {
    name: 'Electrician',
    icon: '⚡',
    title: 'Capture Emergency Electrical Jobs 24/7 & Build Steady Work',
    subtitle: 'Never miss another emergency call. Automated quote system and maintenance reminders keep you consistently booked',
    
    challenges: [
      'Missing emergency calls because you\'re on a job site or in an attic',
      'People need instant quotes but you\'re too busy to respond quickly',
      'Emergency work is unpredictable - feast or famine scheduling',
      'One-time customers never call back for maintenance or upgrades',
      'Spending hours creating quotes and invoices manually',
      'Hard to compete with larger electrical companies for bigger jobs',
      'Commercial clients want immediate response to power issues',
      'Difficult to schedule routine maintenance work around emergencies'
    ],
    
    solutions: [
      '24/7 emergency call capture with instant SMS confirmation - customers know you got their message',
      'Instant quote calculator for common jobs (outlet installation, panel upgrades, lighting) speeds up bookings',
      'Automated maintenance reminder campaigns for annual electrical inspections and safety checks',
      'Online booking for non-emergency work (installations, upgrades, rewiring quotes)',
      'Automated follow-up for quotes converts more estimates into actual jobs',
      'Commercial client portal for priority service requests and job history',
      'Emergency vs scheduled work calendar management keeps your schedule optimized',
      'Automated invoicing and payment reminders get you paid faster'
    ],
    
    features: [
      '24/7 Emergency Request Form with Priority Levels',
      'Instant Quote Calculator for Common Electrical Work',
      'Online Booking for Scheduled Installations',
      'Automated Appointment Reminders & Confirmations',
      'Missed Call Text-Back for Emergency Calls',
      'Annual Electrical Inspection Reminder Campaigns',
      'Commercial Client Priority Portal',
      'Safety Check Follow-Up Sequences',
      'Before/After Photos with Safety Certifications',
      'Google Review Automation',
      'Automated Invoicing & Payment Tracking',
      'Service Area Coverage Map'
    ],
    
    results: [
      'Capture 95% of emergency calls',
      'Respond to enquiries in under 3 minutes',
      '40% convert to annual maintenance contracts',
      'Book 50% more scheduled work',
      'Reduce quote-to-job time by 60%',
      'Average 4.8★ Google rating'
    ],
    
    reviews: [
      {
        author: 'Mark Stevens',
        location: 'Manchester',
        text: 'The emergency call capture is worth its weight in gold. I used to miss 4-5 emergency calls a week because I\'m working in fusebox cupboards with no signal. Now every call gets captured and I text them back within minutes. Booked 11 emergency jobs last week.',
        result: '11 emergency jobs in one week'
      },
      {
        author: 'David Thompson',
        location: 'Birmingham',
        text: 'The instant quote calculator has completely changed how I work. Customers get ballpark pricing immediately for common jobs. My conversion rate has gone from 40% to 75% because I\'m not losing leads to faster-responding competitors.',
        result: '75% quote conversion rate'
      },
      {
        author: 'James Wilson',
        location: 'Leeds',
        text: 'The annual inspection reminders have built me a solid base of recurring work. 127 customers are now on automated annual inspection reminders. That\'s consistent work between emergency jobs, and 40% of them add extra work during the inspection.',
        result: '127 annual inspection contracts'
      },
      {
        author: 'Robert Clarke',
        location: 'Bristol',
        text: 'I work a lot with property managers and the commercial portal is perfect. They can submit maintenance requests 24/7 and see their job history. I\'ve signed 8 property management companies because the system makes me look professional and reliable.',
        result: '8 commercial contracts signed'
      },
      {
        author: 'Paul Hughes',
        location: 'Glasgow',
        text: 'The review automation got me from 9 Google reviews to 94 in 7 months. Now I show up first for "emergency electrician Glasgow" and my phone doesn\'t stop ringing. The reviews mention my fast response time, which brings in even more emergency work.',
        result: '94 Google reviews, #1 for emergency searches'
      },
      {
        author: 'Simon Foster',
        location: 'Liverpool',
        text: 'The automated follow-up for quotes has been brilliant. I used to send a quote and never hear back. Now the system follows up automatically and converts 60% of my quotes into bookings. That\'s an extra £8,500 per month in work I would have lost.',
        result: '£8,500 additional monthly revenue'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Electricians UK | Emergency Call Capture ⚡ Rated 4.9/5"
        description="Capture 95% of emergency calls 24/7. Instant quote calculator, automated maintenance reminders, commercial client portal. Start £5 trial today."
        keywords="CRM for electricians UK, electrician business software, emergency electrician software UK, electrical contractor CRM, electrician customer management, electrician scheduling software UK"
        url="/industries/electrician"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Electrician;
